import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, AuthTokens } from '../../../domain/user';

export interface ActiveClinicContext {
  clinicId: string;
  clinicName: string;
  role: string;
  /** Clinic's ISO 3166-1 alpha-2 country code (from its address), if known. */
  country?: string;
  /** Clinic's city (from its address), if known. */
  city?: string;
  /**
   * Clinic's billing currency (ISO code). Carried on the session so money-formatting surfaces never have to
   * assume one. See `.ai/BusinessDomain/billing-payment.md`, "Currency".
   */
  currency?: string;
}

export interface ClinicMembershipState {
  clinicId: string;
  clinicName: string;
  role: string;
  roles?: string[];
  isOnboardingComplete?: boolean;
  /** Clinic's ISO 3166-1 alpha-2 country code (from its address), if known. */
  country?: string;
  /** Clinic's city (from its address), if known. */
  city?: string;
  /** Clinic's billing currency (ISO code). */
  currency?: string;
}

/**
 * Auth state shape
 */
interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  memberships: ClinicMembershipState[];
  activeContext: ActiveClinicContext | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Initial auth state
 */
const initialState: AuthState = {
  user: null,
  tokens: null,
  memberships: [],
  activeContext: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

/**
 * Auth slice - Manages authentication state
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    /**
     * Set auth error
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Login success - Set user and tokens
     */
    loginSuccess: (
      state,
      action: PayloadAction<{ user: User; tokens: AuthTokens; memberships?: ClinicMembershipState[] }>
    ) => {
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.memberships = action.payload.memberships ?? state.memberships;
      state.activeContext = getActiveContext(state.memberships, state.activeContext);
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Re-sync the user's clinic memberships from a fresh server snapshot (e.g. the /auth/session read on
     * app load), so roles assigned server-side since login become selectable without a re-login. Preserves
     * the currently active role when it is still assignable. An empty snapshot is ignored so a transient
     * failure never clobbers the persisted session.
     */
    membershipsRefreshed: (state, action: PayloadAction<ClinicMembershipState[]>) => {
      if (action.payload.length === 0) return;
      state.memberships = action.payload;
      state.activeContext = getActiveContext(state.memberships, state.activeContext);
    },

    /**
     * Update user data
     */
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },

    /**
     * Update tokens
     */
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      state.tokens = action.payload;
    },

    setActiveContext: (state, action: PayloadAction<ActiveClinicContext>) => {
      state.activeContext = action.payload;
    },

    /**
     * Apply the backend switch-context result: set the active clinic/role and refresh the matching
     * membership's role + selectable roles, so the role switcher stays in sync with server-side changes.
     */
    applySwitchedContext: (
      state,
      action: PayloadAction<{ clinicId: string; clinicName: string; role: string; roles: string[] }>
    ) => {
      const { clinicId, clinicName, role, roles } = action.payload;
      const membership = state.memberships.find((m) => m.clinicId === clinicId);
      state.activeContext = {
        clinicId,
        clinicName,
        role,
        country: membership?.country,
        city: membership?.city,
        currency: membership?.currency,
      };
      if (membership) {
        membership.role = role;
        membership.roles = roles;
      }
    },

    markActiveClinicOnboardingComplete: (state) => {
      const membership = state.memberships.find((item) => item.clinicId === state.activeContext?.clinicId);
      if (membership) membership.isOnboardingComplete = true;
    },

    /**
     * Update access token only (after refresh)
     */
    updateAccessToken: (state, action: PayloadAction<string>) => {
      if (state.tokens) {
        state.tokens.accessToken = action.payload;
      }
    },

    /**
     * Logout - Clear all auth state
     */
    logout: (state) => {
      state.user = null;
      state.tokens = null;
      state.memberships = [];
      state.activeContext = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },
  },
});

/**
 * Export actions
 */
export const {
  setLoading,
  setError,
  loginSuccess,
  membershipsRefreshed,
  setUser,
  setTokens,
  setActiveContext,
  applySwitchedContext,
  markActiveClinicOnboardingComplete,
  updateAccessToken,
  logout,
  clearError,
} = authSlice.actions;

/**
 * Export reducer
 */
export const authReducer = authSlice.reducer;

/**
 * Selectors
 */
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectTokens = (state: { auth: AuthState }) => state.auth.tokens;
export const selectMemberships = (state: { auth: AuthState }) => state.auth.memberships;
export const selectActiveContext = (state: { auth: AuthState }) => state.auth.activeContext;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

function getActiveContext(
  memberships: ClinicMembershipState[],
  existingContext: ActiveClinicContext | null
): ActiveClinicContext | null {
  if (memberships.length === 0) return null;

  // The active role is a *clinic staff* role (e.g. Owner/Doctor), never the
  // account-level user role. Stay on the previously active clinic if it is still
  // linked; otherwise fall back to the first membership.
  const existingMembership = memberships.find((membership) => membership.clinicId === existingContext?.clinicId);
  const membership = existingMembership ?? memberships[0];

  const selectableRoles = membership.roles?.length ? membership.roles : [membership.role];
  // Preserve an already-selected active role if it is still assignable (e.g. across
  // a token refresh); otherwise use the membership's remembered/primary staff role.
  const role =
    existingMembership && existingContext
      ? selectableRoles.find((r) => r.toLowerCase() === existingContext.role.toLowerCase()) ?? membership.role
      : membership.role;

  return {
    clinicId: membership.clinicId,
    clinicName: membership.clinicName,
    role,
    country: membership.country,
    city: membership.city,
    currency: membership.currency,
  };
}
